// src/platform/core/configuration.js

class Configuration {
  constructor() {
    this.values = {
      // =================================================
      // APPLICATION
      // =================================================

      appName: 'NexgbitsIS',

      appVersion:
        process.env.VUE_APP_VERSION ||
        process.env.npm_package_version ||
        '0.1.0',

      environment:
        process.env.NODE_ENV ||
        'development',

      baseUrl:
        process.env.BASE_URL ||
        '/',


      // =================================================
      // PWA
      // =================================================

      pwa: {
        enabled: true,

        /*
         * Relative endpoint used by the optional
         * PWA health checker.
         *
         * The health checker will use the application's
         * EXISTING API connection from @/auth/api.
         *
         * Therefore we do not define another API base URL
         * here.
         */
        healthEndpoint:
          process.env.VUE_APP_API_HEALTH_ENDPOINT ||
          '/api/health',

        healthTimeoutMs: 5000
      },


      // =================================================
      // LOCAL STORAGE
      // =================================================

      storage: {
        dbName: 'NexgbitsIS',
        dbVersion: 1
      },


      // =================================================
      // LOCAL CACHE
      // =================================================

      cache: {
        /*
         * null means cached values do not expire
         * unless the caller supplies a TTL.
         */
        defaultTtl: null
      },


      // =================================================
      // OFFLINE SYNCHRONIZATION
      // =================================================

      sync: {
        maxRetries: 5
      },


      // =================================================
      // PLATFORM UI
      // =================================================

      ui: {
        splash: {
          minimumDisplayMs: 500,
          message: 'Loading NexgbitsIS...'
        }
      }
    }

    this.initialized = false
  }


  /**
   * Merge runtime configuration into the defaults.
   *
   * Useful if configuration is later supplied from:
   *
   * window.__NEXGBITS_CONFIG__
   *
   * or another runtime source.
   */
  init(runtimeConfig = {}) {
    if (this.initialized) {
      return
    }

    this.values =
      this.deepMerge(
        this.values,
        runtimeConfig
      )

    this.initialized = true
  }


  /**
   * Return a configuration value using dot notation.
   *
   * Example:
   *
   * configuration.get('storage.dbName')
   */
  get(
    path,
    defaultValue = null
  ) {
    if (!path) {
      return defaultValue
    }

    const keys =
      path.split('.')

    let current =
      this.values

    for (const key of keys) {
      if (
        current === null ||
        current === undefined ||
        !Object.prototype.hasOwnProperty.call(
          current,
          key
        )
      ) {
        return defaultValue
      }

      current =
        current[key]
    }

    return current
  }


  /**
   * Update one configuration value at runtime.
   */
  set(path, value) {
    if (!path) {
      throw new Error(
        '[Configuration] Configuration path is required.'
      )
    }

    const keys =
      path.split('.')

    let current =
      this.values

    for (
      let i = 0;
      i < keys.length - 1;
      i++
    ) {
      const key =
        keys[i]

      if (
        !current[key] ||
        typeof current[key] !==
          'object'
      ) {
        current[key] = {}
      }

      current =
        current[key]
    }

    current[
      keys[keys.length - 1]
    ] = value

    return value
  }


  /**
   * Check whether a configuration value exists.
   */
  has(path) {
    const sentinel =
      Symbol('missing')

    return (
      this.get(
        path,
        sentinel
      ) !== sentinel
    )
  }


  /**
   * Return a copy of the entire configuration.
   */
  getAll() {
    return this.clone(
      this.values
    )
  }


  /**
   * Is application running in production?
   */
  isProduction() {
    return (
      this.get(
        'environment'
      ) === 'production'
    )
  }


  /**
   * Is application running in development?
   */
  isDevelopment() {
    return (
      this.get(
        'environment'
      ) === 'development'
    )
  }


  /**
   * Resolve a URL using one of the configured
   * base values.
   */
  resolveUrl(
    basePath,
    relativePath = ''
  ) {
    const base =
      this.get(
        basePath,
        ''
      )

    if (!base) {
      return relativePath
    }

    if (!relativePath) {
      return base
    }

    const normalizedBase =
      base.endsWith('/')
        ? base.slice(0, -1)
        : base

    const normalizedPath =
      relativePath.startsWith('/')
        ? relativePath
        : `/${relativePath}`

    return (
      `${normalizedBase}${normalizedPath}`
    )
  }


  /**
   * Deep merge plain configuration objects.
   */
  deepMerge(
    target,
    source
  ) {
    const result = {
      ...target
    }

    Object
      .keys(source || {})
      .forEach(key => {
        const sourceValue =
          source[key]

        const targetValue =
          result[key]

        if (
          this.isPlainObject(
            sourceValue
          ) &&
          this.isPlainObject(
            targetValue
          )
        ) {
          result[key] =
            this.deepMerge(
              targetValue,
              sourceValue
            )
        } else {
          result[key] =
            sourceValue
        }
      })

    return result
  }


  /**
   * Check for a plain JavaScript object.
   */
  isPlainObject(value) {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    )
  }


  /**
   * Return a safe copy of a configuration value.
   */
  clone(value) {
    if (
      typeof structuredClone ===
      'function'
    ) {
      return structuredClone(
        value
      )
    }

    return JSON.parse(
      JSON.stringify(value)
    )
  }
}


const configuration =
  new Configuration()


export default configuration